import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  Send as SendIcon,
  Science as TestIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import unifiedApi from '../../utils/unifiedApi';
import { useCanManageLeague, useIsAdmin, useIsSparesCoordinator } from '../../contexts/PermissionsContext';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const STATUS_OPTIONS = [
  { value: 'Available', label: 'Available', icon: <CheckCircleIcon fontSize="small" />, color: 'success' },
  { value: 'Unavailable', label: 'Not Available', icon: <CancelIcon fontSize="small" />, color: 'error' },
  { value: 'NoResponse', label: 'No Response', icon: <HelpIcon fontSize="small" />, color: 'warning' },
];

const BATCH_STATUS = {
  Pending: { label: 'Scheduled', color: 'info' },
  Processing: { label: 'Sending', color: 'info' },
  Sent: { label: 'Sent', color: 'success' },
  Cancelled: { label: 'Cancelled', color: 'default' },
  Failed: { label: 'Failed', color: 'error' },
};

const RECIPIENT_STATUS = {
  Queued: { label: 'Queued', color: 'default' },
  Sent: { label: 'Delivered', color: 'success' },
  Failed: { label: 'Failed', color: 'error' },
};

const isHtmlEmpty = (html) => {
  if (!html) return true;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  return (parsed.body?.textContent || '').trim().length === 0;
};

const formatDate = (value) => (value ? dayjs(value).format('ddd, MMM D, YYYY') : '');
const formatDateTime = (value) => (value ? `${dayjs(value).format('ddd, MMM D, YYYY')}, ${dayjs(value).format('h:mm a')}` : '');

/**
 * SpareEmailComposer - Lets spares coordinators (and league/tenant admins) email spares filtered by
 * availability for the selected game date, send a test, schedule sends, and review sent history.
 */
const SpareEmailComposer = ({ leagueId, gameDate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const canManageLeague = useCanManageLeague(Number(leagueId));
  const isAdmin = useIsAdmin();
  const isSparesCoordinator = useIsSparesCoordinator();
  const canEmailSpares = canManageLeague || isAdmin || isSparesCoordinator;

  const [statuses, setStatuses] = useState(STATUS_OPTIONS.map((s) => s.value));
  const [goalkeepersOnly, setGoalkeepersOnly] = useState(false);
  const [includeExempt, setIncludeExempt] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const [replyToEmail, setReplyToEmail] = useState('');
  const [ccEmail, setCcEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scheduleSend, setScheduleSend] = useState(false);
  const [scheduledSendDateTime, setScheduledSendDateTime] = useState('');
  const [testEmail, setTestEmail] = useState('');

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sending, setSending] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const allStatusesSelected = statuses.length === STATUS_OPTIONS.length;

  const buildFilter = useCallback(() => ({
    gameDate,
    statuses: allStatusesSelected ? [] : statuses,
    goalkeepersOnly,
    includeExempt,
  }), [gameDate, statuses, allStatusesSelected, goalkeepersOnly, includeExempt]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await unifiedApi.getSpareEmailHistory(leagueId);
      setHistory(data || []);
    } catch (err) {
      console.error('Failed to load spare email history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (!canEmailSpares) return;
    const loadSettings = async () => {
      try {
        const settings = await unifiedApi.getSpareEmailSettings(leagueId);
        setReplyToEmail(settings?.replyToEmail || '');
        setCcEmail(settings?.ccEmail || '');
      } catch (err) {
        console.error('Failed to load spare email settings:', err);
      }
    };
    loadSettings();
    loadHistory();
  }, [canEmailSpares, leagueId, loadHistory]);

  useEffect(() => {
    if (!canEmailSpares || !gameDate) return;
    if (statuses.length === 0) {
      setPreview({ recipientCount: 0, recipients: [] });
      return;
    }
    let cancelled = false;
    const loadPreview = async () => {
      setPreviewLoading(true);
      try {
        const data = await unifiedApi.previewSpareEmail(leagueId, buildFilter());
        if (!cancelled) setPreview(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load recipients.');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    loadPreview();
    return () => { cancelled = true; };
  }, [canEmailSpares, gameDate, leagueId, statuses.length, buildFilter]);

  if (!canEmailSpares) return null;

  const toggleStatus = (value) => {
    setStatuses((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const validate = () => {
    if (!subject.trim()) return 'Subject is required.';
    if (isHtmlEmpty(body)) return 'Message is required.';
    return null;
  };

  const handleSendTest = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSuccess(null);
    setTestSending(true);
    try {
      await unifiedApi.sendSpareEmailTest(leagueId, {
        gameDate,
        subject,
        htmlBody: body,
        replyToEmail: replyToEmail.trim() || null,
        testEmail: testEmail.trim() || null,
      });
      setSuccess(`Test email sent to ${testEmail.trim() || 'your email address'}.`);
    } catch (ex) {
      setError(ex.message || 'Failed to send test email.');
    } finally {
      setTestSending(false);
    }
  };

  const handleSendClick = () => {
    const err = validate()
      || (statuses.length === 0 ? 'Select at least one availability status.' : null)
      || (scheduleSend && !scheduledSendDateTime ? 'Scheduled date and time are required.' : null)
      || (!scheduleSend && !preview?.recipientCount ? 'No spares match the selected filters.' : null);
    if (err) { setError(err); return; }
    setError(null);
    setConfirmOpen(true);
  };

  const handleSendConfirm = async () => {
    setConfirmOpen(false);
    setError(null);
    setSuccess(null);
    setSending(true);
    try {
      const result = await unifiedApi.sendSpareEmail(leagueId, {
        ...buildFilter(),
        subject,
        htmlBody: body,
        replyToEmail: replyToEmail.trim() || null,
        ccEmail: ccEmail.trim() || null,
        scheduledSendDateTime: scheduleSend ? scheduledSendDateTime : null,
      });
      setSuccess(result?.status === 'Pending'
        ? `Email scheduled for ${formatDateTime(result.scheduledSendAt)}.`
        : `Email sent to ${result?.recipientCount ?? 0} spare(s).`);
      setSubject('');
      setBody('');
      setScheduleSend(false);
      setScheduledSendDateTime('');
      loadHistory();
    } catch (ex) {
      setError(ex.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (batchId) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    try {
      await unifiedApi.cancelSpareEmail(leagueId, batchId);
      setSuccess('Scheduled email cancelled.');
      loadHistory();
    } catch (ex) {
      setError(ex.message || 'Failed to cancel scheduled email.');
    }
  };

  const openDetail = async (batchId) => {
    setDetailLoading(true);
    setDetail({ id: batchId });
    try {
      const data = await unifiedApi.getSpareEmailBatch(leagueId, batchId);
      setDetail(data);
    } catch (ex) {
      setError(ex.message || 'Failed to load email details.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const renderBatchStatus = (batch) => {
    const config = BATCH_STATUS[batch.status] || { label: batch.status, color: 'default' };
    return <Chip size="small" label={config.label} color={config.color} />;
  };

  const renderDeliveryCounts = (batch) => {
    if (batch.status === 'Pending' || batch.status === 'Cancelled') return '—';
    const parts = [`${batch.sentCount} delivered`];
    if (batch.failedCount) parts.push(`${batch.failedCount} failed`);
    if (batch.queuedCount) parts.push(`${batch.queuedCount} queued`);
    return `${batch.totalRecipients} · ${parts.join(', ')}`;
  };

  const renderBatchActions = (batch) => (
    <Stack direction="row" spacing={1}>
      <Button size="small" onClick={() => openDetail(batch.id)}>View</Button>
      {batch.status === 'Pending' && (
        <Button size="small" color="error" onClick={() => handleCancelScheduled(batch.id)}>Cancel</Button>
      )}
    </Stack>
  );

  const batchWhen = (batch) => formatDateTime(batch.status === 'Pending' ? batch.scheduledSendAt : (batch.processedAt || batch.createdAt));

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }} data-testid="spare-email-composer">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <EmailIcon color="primary" />
        <Typography variant="h6">Email Spares</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recipients are based on spare availability for {formatDate(gameDate)}. Each spare gets a personal
        &quot;Update My Availability&quot; button.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}

      <Typography variant="subtitle2" gutterBottom>Recipients</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
        {STATUS_OPTIONS.map((option) => {
          const selected = statuses.includes(option.value);
          return (
            <Chip
              key={option.value}
              icon={option.icon}
              label={option.label}
              color={selected ? option.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              onClick={() => toggleStatus(option.value)}
              aria-pressed={selected}
            />
          );
        })}
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0, sm: 2 }}>
        <FormControlLabel
          control={<Switch checked={goalkeepersOnly} onChange={(e) => setGoalkeepersOnly(e.target.checked)} />}
          label="Goalkeepers only"
        />
        <FormControlLabel
          control={<Switch checked={includeExempt} onChange={(e) => setIncludeExempt(e.target.checked)} />}
          label="Include spares exempt from emails"
        />
      </Stack>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {previewLoading ? (
          <CircularProgress size={16} />
        ) : (
          <Typography variant="body2" data-testid="recipient-count">
            <strong>{preview?.recipientCount ?? 0}</strong> spare(s) will receive this email
          </Typography>
        )}
        {preview?.recipientCount > 0 && (
          <Button size="small" onClick={() => setShowRecipients((v) => !v)}>
            {showRecipients ? 'Hide recipients' : 'Show recipients'}
          </Button>
        )}
      </Box>
      <Collapse in={showRecipients && preview?.recipientCount > 0}>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {(preview?.recipients || []).map((r) => (
            <Chip
              key={r.userTenantId}
              size="small"
              variant="outlined"
              label={
                <Link to={`/player/${r.userTenantId}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {r.name}
                </Link>
              }
            />
          ))}
        </Stack>
      </Collapse>

      <Divider sx={{ my: 2 }} />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Reply-to email"
          type="email"
          value={replyToEmail}
          onChange={(e) => setReplyToEmail(e.target.value)}
          fullWidth
          size="small"
          helperText="Replies from spares go here. Saved for next time."
        />
        <TextField
          label="CC email"
          type="email"
          value={ccEmail}
          onChange={(e) => setCcEmail(e.target.value)}
          fullWidth
          size="small"
          helperText="Receives one copy with the recipient list. Saved for next time."
        />
      </Stack>

      <TextField
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        fullWidth
        required
        sx={{ mb: 2 }}
      />

      <Box sx={{ mb: 2, '& .ql-container': { minHeight: 160, fontSize: '1rem' }, '& .ql-editor': { minHeight: 160 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Message</Typography>
        <ReactQuill
          theme="snow"
          value={body}
          onChange={setBody}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder="Write your message..."
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          The message is wrapped in the standard email template.
        </Typography>
      </Box>

      <FormControlLabel
        control={<Switch checked={scheduleSend} onChange={(e) => setScheduleSend(e.target.checked)} />}
        label="Schedule for later"
      />
      {scheduleSend && (
        <TextField
          label="Send date and time"
          type="datetime-local"
          value={scheduledSendDateTime}
          onChange={(e) => setScheduledSendDateTime(e.target.value)}
          fullWidth
          required
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 1, mb: 1 }}
          helperText="Interpreted in your organization's timezone. Recipients are re-checked against availability when it sends."
        />
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 2 }}>
        <TextField
          label="Test email address"
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          size="small"
          placeholder="Defaults to your email"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          startIcon={testSending ? <CircularProgress size={16} /> : <TestIcon />}
          disabled={testSending}
          onClick={handleSendTest}
          fullWidth={isMobile}
        >
          Send Test
        </Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={sending}
          onClick={handleSendClick}
          fullWidth={isMobile}
        >
          {scheduleSend ? 'Schedule Email' : 'Send Email'}
        </Button>
      </Stack>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Sent &amp; Scheduled Emails</Typography>
      {historyLoading && history.length === 0 ? (
        <CircularProgress size={20} />
      ) : history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No emails have been sent to spares in this league yet.</Typography>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {history.map((batch) => (
            <Paper key={batch.id} variant="outlined" sx={{ p: 1.5 }} data-testid={`spare-email-batch-${batch.id}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Typography variant="body2" fontWeight="bold" sx={{ wordBreak: 'break-word' }}>{batch.subject}</Typography>
                {renderBatchStatus(batch)}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">{batchWhen(batch)}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Game date {formatDate(batch.gameDate)} · {batch.filterSummary}
              </Typography>
              {batch.sentByName && (
                <Typography variant="caption" color="text.secondary" display="block">By {batch.sentByName}</Typography>
              )}
              <Typography variant="caption" display="block">{renderDeliveryCounts(batch)}</Typography>
              {batch.lastError && <Typography variant="caption" color="error" display="block">{batch.lastError}</Typography>}
              <Box sx={{ mt: 1 }}>{renderBatchActions(batch)}</Box>
            </Paper>
          ))}
        </Stack>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Game Date</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Sent By</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((batch) => (
                <TableRow key={batch.id} data-testid={`spare-email-batch-${batch.id}`}>
                  <TableCell>{batchWhen(batch)}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{batch.subject}</TableCell>
                  <TableCell>{formatDate(batch.gameDate)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{batch.filterSummary}</Typography>
                    <Typography variant="caption" color="text.secondary">{renderDeliveryCounts(batch)}</Typography>
                  </TableCell>
                  <TableCell>{batch.sentByName || '—'}</TableCell>
                  <TableCell>
                    {renderBatchStatus(batch)}
                    {batch.lastError && (
                      <Typography variant="caption" color="error" display="block">{batch.lastError}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{renderBatchActions(batch)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{scheduleSend ? 'Confirm Schedule' : 'Confirm Send'}</DialogTitle>
        <DialogContent>
          {scheduleSend ? (
            <Typography>
              Schedule <strong>&quot;{subject}&quot;</strong> for {formatDateTime(scheduledSendDateTime)}?
              Spares matching the filters at that time will receive it.
            </Typography>
          ) : (
            <Typography>
              Send <strong>&quot;{subject}&quot;</strong> to {preview?.recipientCount ?? 0} spare(s)?
            </Typography>
          )}
          {ccEmail.trim() && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, wordBreak: 'break-all' }}>
              A copy will be sent to {ccEmail.trim()}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendConfirm}>{scheduleSend ? 'Schedule' : 'Send'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ wordBreak: 'break-word' }}>{detail?.subject || 'Email details'}</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail?.recipients ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                {batchWhen(detail)} · Game date {formatDate(detail.gameDate)} · {detail.filterSummary}
              </Typography>
              {(detail.replyToEmail || detail.ccEmail) && (
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {detail.replyToEmail && `Reply-to: ${detail.replyToEmail}`}
                  {detail.replyToEmail && detail.ccEmail && ' · '}
                  {detail.ccEmail && `CC: ${detail.ccEmail}`}
                </Typography>
              )}
              <Box
                sx={{ my: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: detail.htmlBody }}
              />
              {detail.recipients.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recipients yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  {detail.recipients.map((r) => {
                    const config = RECIPIENT_STATUS[r.status] || { label: r.status, color: 'default' };
                    return (
                      <Stack
                        key={r.userTenantId}
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={0.5}
                      >
                        <Box>
                          <Link to={`/player/${r.userTenantId}`} style={{ color: 'inherit' }}>
                            <Typography variant="body2">{r.name}</Typography>
                          </Link>
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{r.email}</Typography>
                          {r.errorMessage && (
                            <Typography variant="caption" color="error" display="block">{r.errorMessage}</Typography>
                          )}
                        </Box>
                        <Chip size="small" label={config.label} color={config.color} />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SpareEmailComposer;
